import { normalizeDictationLanguage } from '../lib/voice'
import { auth } from '../utils/firebase'

interface TranscribeAudioOptions {
  language?: string
  mimeType?: string
}

interface TranscriptionApiResponse {
  text?: string
  error?: string
  details?: string
}

const encodeAudioBlob = async (audioBlob: Blob) => {
  const audioBuffer = typeof audioBlob.arrayBuffer === 'function'
    ? await audioBlob.arrayBuffer()
    : await new Response(audioBlob).arrayBuffer()
  const bytes = new Uint8Array(audioBuffer)
  const chunkSize = 0x8000
  let binary = ''

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize))
  }

  return window.btoa(binary)
}

export async function transcribeAudio(audioBlob: Blob, options: TranscribeAudioOptions = {}) {
  if (audioBlob.size === 0) {
    throw new Error('voice_recording_failed')
  }

  if (!auth.currentUser) {
    throw new Error('voice_auth_required')
  }

  const token = await auth.currentUser.getIdToken()
  const audioBase64 = await encodeAudioBlob(audioBlob)
  const body = {
    audioBase64,
    mimeType: options.mimeType || audioBlob.type || 'audio/webm',
    language: normalizeDictationLanguage(options.language)
  }

  const isProduction = window.location.hostname === 'tydy.app'
  let response: Response | undefined

  try {
    response = await fetch('/api/transcribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    })
  } catch (networkError) {
    if (!isProduction) {
      throw networkError
    }

    const fallbacks = [
      'https://api-dvk3qscrwq-uc.a.run.app/transcribe',
      'https://us-central1-plnnr-11ee8.cloudfunctions.net/api/transcribe'
    ]

    let lastError: unknown = networkError

    for (const url of fallbacks) {
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        })
        break
      } catch (fallbackError) {
        lastError = fallbackError
      }
    }

    if (!response) {
      throw lastError
    }
  }

  if (!response.ok) {
    const errorPayload = await response.json().catch(() => null) as TranscriptionApiResponse | null
    throw new Error(errorPayload?.error || 'voice_transcription_failed')
  }

  const data = await response.json() as TranscriptionApiResponse
  const text = data.text?.trim()

  if (!text) {
    throw new Error('voice_transcription_empty')
  }

  return text
}
